import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { logout } from "../api/endpoints";
import type { UserProfile } from "../api/types";

const ROLE_LABELS: Record<string, string> = {
  RECEPTIONIST: "Reception",
  DOCTOR: "Doctor",
  CLINIC_ADMIN: "Administrator",
  SUPERADMIN: "Superadmin",
};

/**
 * Who is signed in, and the way out.
 *
 * Worth a menu rather than a bare "Sign out" link for one reason that is not
 * decoration: clinic tablets are shared. A receptionist finishing a shift, a
 * doctor picking up the same tablet, a superadmin visiting somebody else's
 * clinic — all three look identical on screen, and every one of them is about
 * to act as whoever was left signed in. The name is the point; the sign-out is
 * underneath it.
 */
export default function UserMenu({ profile }: { profile: UserProfile | null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // A click anywhere else closes it, and so does Escape. Both, because a menu
  // that can only be dismissed by the button that opened it is a menu people
  // navigate away from instead.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const name = profile?.full_name ?? "";
  const role = profile ? (ROLE_LABELS[profile.role] ?? profile.role) : "";

  return (
    <div className="usermenu" ref={box}>
      <button
        className="usermenu__button"
        aria-expanded={open}
        onClick={() => { setOpen((was) => !was); }}
      >
        <span className="usermenu__avatar" aria-hidden="true">{initials(name)}</span>
        <span className="usermenu__who">
          <span className="usermenu__name">{name || "Signed in"}</span>
          <span className="usermenu__role">{role}</span>
        </span>
      </button>

      {/* A disclosure, not an ARIA menu: there are no arrow-key semantics here
          to back that role up, and announcing a menu that does not behave like
          one is worse for a screen reader than announcing nothing. */}
      {open && (
        <div className="usermenu__panel">
          <div className="usermenu__identity">
            <div className="usermenu__name">{name}</div>
            {/* The address, not just the name: two people called Patil at one
                clinic is not a hypothetical, and this is the field that tells
                a shared tablet apart. */}
            <div className="muted">{profile?.email}</div>
            <div className="tag tag--action" style={{ marginTop: 8 }}>{role}</div>
          </div>
          <button
            className="usermenu__item"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              // Navigate rather than reload: the session is already cleared,
              // and a full page load costs the bundle again on a tablet that
              // is about to be signed straight back into.
              void logout().finally(() => { void navigate("/login", { replace: true }); });
            }}
          >
            {busy ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Two letters at most: a circle is 36px and three initials in it is a smudge. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}
